/* Cart drawer and cart page: open the drawer, change quantities, remove lines, quick add.
   Without JavaScript the header link goes to /cart and the cart page is a plain form. */
(() => {
  'use strict';

  if (window.SukatCart) return;
  window.SukatCart = true;

  const routes = (window.SukatConfig && window.SukatConfig.routes) || {};
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const getDrawer = () => document.querySelector('[data-cart-drawer]');
  const cartRoots = () => document.querySelectorAll('[data-cart-root]');
  const trimSlash = (path) => (path || '').replace(/\/$/, '');
  const onCartPage = () => Boolean(routes.cart) && trimSlash(window.location.pathname) === trimSlash(routes.cart);

  function sectionIds() {
    const ids = Array.from(cartRoots(), (root) => root.dataset.sectionId);
    const header = window.Sukat ? window.Sukat.sectionId('header') : null;
    if (header) ids.push(header);
    return Array.from(new Set(ids.filter(Boolean))).slice(0, 5);
  }

  function statusOf(root) {
    return root ? root.querySelector('[data-cart-status]') : null;
  }

  function announce(root, message) {
    const status = statusOf(root);
    if (status) status.textContent = message || '';
  }

  function fallbackMessage(root) {
    const status = statusOf(root);
    return status ? status.dataset.fallback || '' : '';
  }

  function setBusy(busy) {
    cartRoots().forEach((root) => {
      if (busy) root.setAttribute('aria-busy', 'true');
      else root.removeAttribute('aria-busy');
      root.querySelectorAll('[data-cart-checkout]').forEach((button) => {
        if (busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    });
  }

  function openDrawer(opener) {
    const drawer = getDrawer();
    if (!drawer || onCartPage() || !window.Sukat) return;
    window.Sukat.openDialog(drawer, opener || document.activeElement);
  }

  /* Swap marked regions of every cart root from Section Rendering HTML, keeping the dialog itself. */
  function render(sections) {
    if (!sections) return;
    const activeId = document.activeElement ? document.activeElement.id : '';

    /* Steppers and remove links have no id; remember them by line key so focus survives the swap. */
    let lineFocus = null;
    const active = document.activeElement;
    const activeLine = active && active.closest ? active.closest('[data-cart-line]') : null;
    if (activeLine) {
      if (active.hasAttribute('data-cart-step')) {
        lineFocus = { lineKey: activeLine.dataset.lineKey, selector: `[data-cart-step="${active.dataset.cartStep}"]` };
      } else if (active.hasAttribute('data-cart-remove')) {
        lineFocus = { lineKey: activeLine.dataset.lineKey, selector: '[data-cart-remove]' };
      }
    }

    cartRoots().forEach((root) => {
      const html = sections[root.dataset.sectionId];
      if (!html) return;
      const next = parse(html).querySelector('[data-cart-root]');
      if (!next) return;

      const note = root.querySelector('[data-cart-note]');
      const noteValue = note ? note.value : null;

      root.querySelectorAll('[data-cart-region]').forEach((region) => {
        const incoming = next.querySelector(`[data-cart-region="${region.dataset.cartRegion}"]`);
        if (!incoming) return;
        region.innerHTML = incoming.innerHTML;
        region.hidden = incoming.hidden;
      });

      const freshNote = root.querySelector('[data-cart-note]');
      if (freshNote && noteValue !== null) freshNote.value = noteValue;
    });

    if (activeId) {
      const again = document.getElementById(activeId);
      if (again && again !== document.activeElement) again.focus({ preventScroll: true });
    }

    /* Only when focus was lost and the line still exists; removed lines are left to focusAfterRemoval. */
    const current = document.activeElement;
    const focusLost = !current || current === document.body || !current.isConnected;
    if (lineFocus && focusLost) {
      const line = Array.from(document.querySelectorAll('[data-cart-line]')).find(
        (candidate) => candidate.dataset.lineKey === lineFocus.lineKey
      );
      const target = line ? line.querySelector(lineFocus.selector) : null;
      if (target) target.focus({ preventScroll: true });
    }
  }

  function dispatchUpdate(detail) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  }

  /* After a removal, focus the line that moved into its place, else the one above, else the empty-state link. */
  function focusAfterRemoval(root, position) {
    const lines = root.querySelectorAll('[data-cart-line]');
    const target = lines[position] || lines[position - 1];
    const focusable = target
      ? target.querySelector('[data-cart-focus]')
      : root.querySelector('[data-cart-empty-focus]') || root.querySelector('[tabindex="-1"]');
    if (focusable) focusable.focus();
  }

  /* ---------- Quantity changes and removals (serialised, by line key) ---------- */
  let queue = Promise.resolve();
  const timers = new Map();

  function queueChange(root, key, quantity, removing) {
    queue = queue.then(() => change(root, key, quantity, removing)).catch(() => {});
    return queue;
  }

  async function change(root, key, quantity, removing) {
    const lines = Array.from(root.querySelectorAll('[data-cart-line]'));
    const position = lines.findIndex((line) => line.dataset.lineKey === key);
    const ids = sectionIds();
    announce(root, '');
    setBusy(true);

    try {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: ids.join(','),
          sections_url: window.location.pathname
        })
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        /* 422 stock limits arrive here; restore the real quantities from the server. */
        announce(root, json.description || json.message || fallbackMessage(root));
        const fresh = window.Sukat ? await window.Sukat.fetchSections(ids).catch(() => null) : null;
        if (fresh) dispatchUpdate({ sections: fresh, source: 'cart' });
        return;
      }

      dispatchUpdate({ sections: json.sections, cart: json, source: 'cart' });
      if (removing) focusAfterRemoval(root, Math.max(position, 0));
    } catch (error) {
      announce(root, fallbackMessage(root));
    } finally {
      setBusy(false);
    }
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    const root = input.closest('[data-cart-root]');
    const line = input.closest('[data-cart-line]');
    if (!root || !line || !routes.cartChange) return;

    const key = line.dataset.lineKey;
    clearTimeout(timers.get(key));
    timers.set(
      key,
      setTimeout(() => {
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        queueChange(root, key, quantity, quantity === 0);
      }, 300)
    );
  });

  /* ---------- Clicks: steppers, remove, header trigger ---------- */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-cart-step]');
    if (step) {
      const input = step.parentElement.querySelector('[data-cart-quantity]');
      if (!input) return;
      const value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.cartStep));
      input.value = String(value);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      const root = remove.closest('[data-cart-root]');
      const line = remove.closest('[data-cart-line]');
      if (!root || !line || !routes.cartChange) return;
      event.preventDefault();
      queueChange(root, line.dataset.lineKey, 0, true);
      return;
    }

    const trigger = event.target.closest('[data-cart-trigger]');
    if (trigger) {
      if (!getDrawer() || onCartPage() || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openDrawer(trigger);
      if (window.Sukat) {
        window.Sukat.fetchSections(sectionIds())
          .then((sections) => dispatchUpdate({ sections, source: 'refresh' }))
          .catch(() => {});
      }
    }
  });

  /* ---------- Submits: quick add on product cards; block cart forms while busy ---------- */
  async function quickAdd(event, form) {
    const drawer = getDrawer();
    if (!drawer || !routes.cartAdd) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const body = new FormData(form);
    body.append('sections', sectionIds().join(','));
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        openDrawer(button);
        announce(drawer, json.description || json.message || fallbackMessage(drawer));
        return;
      }
      announce(drawer, '');
      dispatchUpdate({ sections: json.sections, item: json, source: 'quick-add' });
    } catch (error) {
      openDrawer(button);
      announce(drawer, fallbackMessage(drawer));
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  document.addEventListener('submit', (event) => {
    const quick = event.target.closest('form[data-quick-add]');
    if (quick) {
      quickAdd(event, quick);
      return;
    }
    const cartForm = event.target.closest('[data-cart-form]');
    const root = cartForm ? cartForm.closest('[data-cart-root]') : null;
    if (root && root.getAttribute('aria-busy') === 'true') event.preventDefault();
  });

  /* ---------- One render path for every cart change ---------- */
  document.addEventListener('cart:updated', (event) => {
    const detail = event.detail || {};
    render(detail.sections);
    if (detail.source === 'product-form' || detail.source === 'quick-add') {
      announce(getDrawer(), '');
      openDrawer(document.activeElement);
    }
  });

  /* If the opener was re-rendered (header icon swap), return focus to the current cart link. */
  document.addEventListener(
    'close',
    (event) => {
      if (event.target !== getDrawer()) return;
      window.setTimeout(() => {
        if (document.activeElement && document.activeElement !== document.body) return;
        const trigger = document.querySelector('[data-cart-trigger]');
        if (trigger) trigger.focus();
      }, 0);
    },
    true
  );

  /* Back/forward cache: a restored page may show a stale cart, so refresh it. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !window.Sukat) return;
    window.Sukat.fetchSections(sectionIds())
      .then((sections) => dispatchUpdate({ sections, source: 'refresh' }))
      .catch(() => {});
  });

  /* Theme editor: show the drawer while its section is selected */
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector('[data-cart-drawer]');
    if (drawer && !drawer.open && window.Sukat) window.Sukat.openDialog(drawer);
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = event.target.querySelector('[data-cart-drawer]');
    if (drawer && drawer.open) drawer.close();
  });
})();
